import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppointments, Appointment } from '../../crm-appointments/useData';
import { useTodos, Todo } from '../../crm-todos/useData';
import { useTheme } from '../../../contexts/ThemeContext';

export interface CombinedCalendarWidgetProps {
  currentDate?: Date;
}

const CombinedCalendarWidget: React.FC<CombinedCalendarWidgetProps> = ({ currentDate = new Date() }) => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { appointments = [], isLoading: appointmentsLoading } = useAppointments();
  const { data: todos = [], isLoading: todosLoading } = useTodos();

  const [viewDate, setViewDate] = React.useState(currentDate);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDayOfWeek = firstDay.getDay();

  const appointmentsByDate = React.useMemo(() => {
    const map = new Map<string, Appointment[]>();
    (appointments || []).forEach((apt: Appointment) => {
      if (apt.clientAppScheduleDateTime) {
        const date = new Date(apt.clientAppScheduleDateTime);
        const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        const existing = map.get(dateKey) || [];
        map.set(dateKey, [...existing, apt]);
      }
    });
    return map;
  }, [appointments]);

  const todosByDate = React.useMemo(() => {
    const map = new Map<string, Todo[]>();
    (todos || []).forEach((todo: Todo) => {
      if (todo.todoDueDate) {
        const dateKey = todo.todoDueDate.slice(0, 10);
        const existing = map.get(dateKey) || [];
        map.set(dateKey, [...existing, todo]);
      }
    });
    return map;
  }, [todos]);

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const goToPreviousMonth = () => setViewDate(new Date(year, month - 1, 1));
  const goToNextMonth = () => setViewDate(new Date(year, month + 1, 1));
  const goToToday = () => setViewDate(new Date());

  const isToday = (day: number) => {
    const today = new Date();
    return day === today.getDate() && month === today.getMonth() && year === today.getFullYear();
  };

  const getAppointmentsForDay = (day: number) => {
    const dateKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return appointmentsByDate.get(dateKey) || [];
  };

  const getTodosForDay = (day: number) => {
    const dateKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return todosByDate.get(dateKey) || [];
  };

  const isLoading = appointmentsLoading || todosLoading;

  if (isLoading) {
    return (
      <div className={`${colors.surface} rounded-xl shadow-sm p-6 border ${colors.border}`}>
        <div className="h-64 flex items-center justify-center">
          <div className="animate-pulse text-gray-400">Loading calendar...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${colors.surface} rounded-xl shadow-sm p-6 border ${colors.border}`}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
        <h2 className={`text-base sm:text-lg font-semibold ${colors.text}`}>Appointments & Todos</h2>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => navigate('/appointments_calendar_view')}
            className="text-sm text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300 font-medium"
          >
            Appointments
          </button>
          <span className="text-gray-300 dark:text-gray-600">|</span>
          <button
            onClick={() => navigate('/todos_monthly_view')}
            className="text-sm text-amber-600 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300 font-medium"
          >
            Todos
          </button>
        </div>
      </div>

      <div className="mb-4 flex items-center justify-between">
        <button
          onClick={goToPreviousMonth}
          className={`p-2.5 sm:p-2 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center rounded-lg transition-colors touch-manipulation ${colors.interactiveHover}`}
          aria-label="Previous month"
        >
          <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="flex items-center gap-2">
          <h3 className={`text-base font-semibold ${colors.text}`}>
            {monthNames[month]} {year}
          </h3>
          <button
            onClick={goToToday}
            className="px-3 py-1 text-xs text-teal-600 hover:bg-teal-50 dark:text-teal-400 dark:hover:bg-teal-900/20 rounded-md transition-colors"
          >
            Today
          </button>
        </div>
        <button
          onClick={goToNextMonth}
          className={`p-2.5 sm:p-2 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center rounded-lg transition-colors touch-manipulation ${colors.interactiveHover}`}
          aria-label="Next month"
        >
          <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      <div className="overflow-x-auto -mx-1 px-1 sm:mx-0 sm:px-0 touch-pan-x" style={{ minWidth: 0 }}>
        <div className="grid grid-cols-7 gap-0.5 sm:gap-1 mb-2 min-w-[240px] w-max max-w-full mx-auto">
          {dayNames.map((day) => (
            <div key={day} className="text-center text-[10px] sm:text-xs font-medium text-gray-500 dark:text-gray-400 py-0.5 sm:py-1">
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-0.5 sm:gap-1 min-w-[240px] w-max max-w-full mx-auto">
        {Array.from({ length: startingDayOfWeek }).map((_, index) => (
          <div key={`empty-${index}`} className="aspect-square min-w-[28px] sm:min-w-[32px]" />
        ))}
        {Array.from({ length: daysInMonth }).map((_, index) => {
          const day = index + 1;
          const dayAppointments = getAppointmentsForDay(day);
          const dayTodos = getTodosForDay(day);
          const today = isToday(day);
          const hasAny = dayAppointments.length > 0 || dayTodos.length > 0;

          return (
            <div
              key={day}
              onClick={() => (hasAny ? navigate('/todos_monthly_view') : undefined)}
              className={`aspect-square min-w-[28px] sm:min-w-[32px] p-0.5 sm:p-1 border rounded transition-colors ${
                hasAny ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50' : ''
              } ${
                today
                  ? 'bg-teal-50 dark:bg-teal-900/20 border-teal-300 dark:border-teal-700'
                  : `border-gray-200 dark:border-gray-600`
              }`}
            >
              <div className={`text-[10px] sm:text-xs font-medium mb-0.5 ${today ? 'text-teal-600 dark:text-teal-400' : colors.text}`}>
                {day}
              </div>
              <div className="space-y-0.5">
                {dayAppointments.length > 0 && (
                  <>
                    {dayAppointments.slice(0, 1).map((apt, idx) => (
                      <div
                        key={`apt-${idx}`}
                        className="h-1 bg-teal-500 dark:bg-teal-600 rounded text-[0px]"
                        title={apt.clientAppName || 'Appointment'}
                      />
                    ))}
                    {dayAppointments.length > 1 && (
                      <div className="text-[0.55rem] text-teal-600 dark:text-teal-400 font-medium">+{dayAppointments.length - 1}</div>
                    )}
                  </>
                )}
                {dayTodos.length > 0 && (
                  <>
                    {dayTodos.slice(0, 1).map((todo) => (
                      <div
                        key={todo.todoId}
                        className="h-1 bg-amber-500 dark:bg-amber-600 rounded text-[0px]"
                        title={todo.todoDescr || 'Todo'}
                      />
                    ))}
                    {dayTodos.length > 1 && (
                      <div className="text-[0.55rem] text-amber-600 dark:text-amber-400 font-medium">+{dayTodos.length - 1}</div>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-4 mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
        <span className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
          <span className="w-2 h-2 rounded bg-teal-500" /> Appointments
        </span>
        <span className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
          <span className="w-2 h-2 rounded bg-amber-500" /> Todos
        </span>
      </div>
    </div>
  );
};

export default CombinedCalendarWidget;
